import React, { useEffect, useState, useMemo } from "react";
import { X, Plus, Minus, ArrowRight, Loader2, UserPlus, Clock, ShieldOff } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useProducts } from "../hooks/useCatalog";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartSidebar = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, lastAdded } = useCart();
  const { user, loading: authLoading } = useAuth();
  const { products } = useProducts();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  const [acceptNoRefund, setAcceptNoRefund] = useState(false);

  // Map productId → live product for stock & processing_time
  const productMap = useMemo(() => {
    const m = {};
    (products || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [products]);

  const anyOutOfStock = items.some((it) => productMap[it.id]?.stock_status === "out_of_stock");
  const processingTimes = useMemo(() => {
    const times = new Set();
    items.forEach((it) => {
      const t = productMap[it.id]?.processing_time || "3-5 business days";
      times.add(t);
    });
    return Array.from(times);
  }, [items, productMap]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const goSignUp = () => {
    // Save intent so the cart re-opens automatically post-auth
    try { sessionStorage.setItem("wiu_reopen_cart", "1"); } catch (_e) { /* ignore */ }
    setIsOpen(false);
    navigate("/register", { state: { from: "/#cart" } });
  };

  const goCompleteProfile = () => {
    try { sessionStorage.setItem("wiu_reopen_cart", "1"); } catch (_e) { /* ignore */ }
    setIsOpen(false);
    navigate("/complete-profile?next=cart");
  };

  const missingProfile = !!user && (
    !user.address || !user.address.line1 || !user.address.city ||
    !user.address.state || !user.address.postal_code || !user.address.country ||
    !user.phone || !user.email
  );

  const handleCheckout = async () => {
    if (items.length === 0 || checkingOut) return;
    if (!user) { goSignUp(); return; }
    if (missingProfile) { goCompleteProfile(); return; }
    if (anyOutOfStock) { toast.error("Remove out-of-stock items to continue."); return; }
    if (!acceptNoRefund) { toast.error("Please accept the No Refund policy to continue."); return; }
    setCheckingOut(true);
    try {
      const payload = {
        items: items.map((i) => ({ productId: i.id, qty: i.qty, label: i.name })),
        origin_url: window.location.origin,
        accept_no_refund: true,
      };
      const { data } = await axios.post(`${API}/payments/checkout`, payload, { withCredentials: true });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout. Try again.");
        setCheckingOut(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Checkout failed. Try again.";
      toast.error(msg);
      setCheckingOut(false);
    }
  };

  return (
    <>
      <div
        onClick={() => setIsOpen(false)}
        className={`fixed inset-0 bg-charcoal-deep/40 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-charcoal z-50 border-l border-off-white/15 flex flex-col transition-transform duration-500 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(0.2,0.9,0.3,1.2)" }}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-off-white/10">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose">// CART</p>
            <h3 className="font-serif-display italic text-2xl mt-1 text-off-white">Your Stack</h3>
          </div>
          <button onClick={() => setIsOpen(false)} className="text-off-white/70 hover:text-rose transition-colors-only">
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4">
              <div className="w-16 h-16 border border-off-white/20 flex items-center justify-center">
                <span className="font-mono text-xs text-off-white/50">EMPTY</span>
              </div>
              <p className="text-off-white/70 font-light">Your stack is empty.</p>
              <button onClick={() => setIsOpen(false)} className="font-mono text-[11px] tracking-[0.25em] text-rose kinetic-link">
                START SHOPPING →
              </button>
            </div>
          ) : (
            <ul className="flex flex-col gap-5">
              {items.map((item) => {
                const live = productMap[item.id];
                const isOOS = live?.stock_status === "out_of_stock";
                const proc = live?.processing_time || "3-5 business days";
                return (
                <li key={item.id} className={`flex gap-4 pb-5 border-b border-off-white/10 ${lastAdded === item.id ? "whip-in" : ""}`}>
                  <div className="w-20 h-24 bg-charcoal-3 shrink-0 overflow-hidden flex items-center justify-center">
                    <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <p className="text-sm text-off-white leading-tight font-medium">{item.name}</p>
                    <p className="font-mono text-[11px] text-rose mt-1">${item.price.toFixed(2)}</p>
                    <p className="font-mono text-[10px] tracking-[0.2em] text-off-white/50 mt-0.5 flex items-center gap-1">
                      <Clock size={10} strokeWidth={1.5} /> {proc.toUpperCase()}
                    </p>
                    {isOOS && (
                      <p className="font-mono text-[10px] tracking-[0.25em] text-rose-deep mt-1" data-testid={`oos-line-${item.id}`}>
                        // OUT OF STOCK — REMOVE TO CHECKOUT
                      </p>
                    )}
                    <div className="mt-auto flex items-center justify-between">
                      <div className="inline-flex items-center border border-off-white/20">
                        <button onClick={() => updateQty(item.id, item.qty - 1)} className="w-7 h-7 flex items-center justify-center text-off-white/70 hover:text-rose transition-colors-only"><Minus size={12} /></button>
                        <span className="font-mono text-xs w-7 text-center text-off-white">{item.qty}</span>
                        <button onClick={() => updateQty(item.id, item.qty + 1)} className="w-7 h-7 flex items-center justify-center text-off-white/70 hover:text-rose transition-colors-only"><Plus size={12} /></button>
                      </div>
                      <button onClick={() => removeItem(item.id)} className="font-mono text-[10px] tracking-[0.2em] text-off-white/50 hover:text-rose uppercase transition-colors-only">Remove</button>
                    </div>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-off-white/10 px-6 py-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] tracking-[0.25em] text-off-white/60">SUBTOTAL</span>
              <span className="font-serif-display italic text-2xl text-off-white">${subtotal.toFixed(2)}</span>
            </div>
            <p className="font-mono text-[10px] tracking-[0.2em] text-off-white/45">SHIPPING & TAXES CALCULATED AT CHECKOUT</p>

            {processingTimes.length > 0 && (
              <div className="border border-off-white/10 p-3 bg-off-white/[0.02] flex items-start gap-2" data-testid="cart-processing-time">
                <Clock size={12} className="text-rose mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-1">// PROCESSING TIME</p>
                  <p className="text-off-white/80 text-xs leading-relaxed">
                    {processingTimes.join(" · ")}. Ships after processing.
                  </p>
                </div>
              </div>
            )}

            <div className="border border-rose/40 bg-rose/[0.06] p-3" data-testid="no-refund-terms-block">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptNoRefund}
                  onChange={(e) => setAcceptNoRefund(e.target.checked)}
                  className="mt-1 shrink-0 accent-rose"
                  data-testid="accept-no-refund-checkbox"
                />
                <span className="text-off-white/85 text-xs leading-relaxed">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-rose mr-1"><ShieldOff size={10} className="inline mr-1" />NO REFUND POLICY ·</span>
                  I acknowledge and accept that <strong>all sales are final</strong>. Whip It Up does not issue refunds or accept returns on any goods. Read the full policy on any product page.
                </span>
              </label>
            </div>

            {!user && !authLoading && (
              <div className="border border-rose/30 bg-rose/5 p-3" data-testid="checkout-signup-notice">
                <p className="font-mono text-[10px] tracking-[0.3em] text-rose flex items-center gap-2 mb-1">
                  <UserPlus size={12} /> ACCOUNT REQUIRED
                </p>
                <p className="text-off-white/75 text-xs leading-relaxed">
                  Sign up to check out — track shipments, reorder in one click, and leave verified reviews.
                </p>
              </div>
            )}
            {user && missingProfile && (
              <div className="border border-rose/30 bg-rose/5 p-3" data-testid="checkout-address-notice">
                <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-1">// PROFILE INCOMPLETE</p>
                <p className="text-off-white/75 text-xs leading-relaxed">
                  Add your shipping address and phone so we can process and ship your order.
                </p>
              </div>
            )}
            {user && !missingProfile && anyOutOfStock && (
              <div className="border border-rose/50 bg-rose/10 p-3" data-testid="checkout-oos-notice">
                <p className="font-mono text-[10px] tracking-[0.3em] text-rose">// OUT OF STOCK IN CART — REMOVE TO CONTINUE</p>
              </div>
            )}
            <button
              onClick={handleCheckout}
              disabled={
                checkingOut || authLoading ||
                (user && !missingProfile && !anyOutOfStock && !acceptNoRefund) ||
                (user && !missingProfile && anyOutOfStock)
              }
              className="w-full h-12 bg-off-white text-charcoal font-mono text-[12px] tracking-[0.25em] font-semibold flex items-center justify-center gap-2 hover:bg-rose hover:text-off-white transition-colors-only disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="checkout-btn"
            >
              {checkingOut ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> REDIRECTING…
                </>
              ) : !user && !authLoading ? (
                <>
                  <UserPlus size={16} /> SIGN UP TO CHECKOUT
                </>
              ) : user && missingProfile ? (
                <>
                  COMPLETE PROFILE <ArrowRight size={16} />
                </>
              ) : anyOutOfStock ? (
                <>REMOVE OUT-OF-STOCK ITEMS</>
              ) : !acceptNoRefund ? (
                <>ACCEPT POLICY TO CONTINUE</>
              ) : (
                <>
                  CHECKOUT <ArrowRight size={16} />
                </>
              )}
            </button>
            {!user && !authLoading && (
              <p className="font-mono text-[10px] tracking-[0.25em] text-off-white/50 text-center">
                ALREADY HAVE AN ACCOUNT?{" "}
                <Link
                  to="/login"
                  onClick={() => { try { sessionStorage.setItem("wiu_reopen_cart", "1"); } catch (_e) { /* ignore */ } setIsOpen(false); }}
                  className="text-rose hover:text-off-white transition-colors-only"
                  data-testid="checkout-login-link"
                >
                  SIGN IN →
                </Link>
              </p>
            )}
            <Link
              to="/shop"
              onClick={() => setIsOpen(false)}
              className="block text-center font-mono text-[11px] tracking-[0.25em] text-off-white/60 hover:text-off-white kinetic-link"
            >
              CONTINUE SHOPPING
            </Link>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartSidebar;
